import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useBriefingRuns, useBriefingStats } from '../../hooks/useAdmin';
import { formatDate, sortRows, toggleDir } from './adminUtils';

const STATUS_COLORS = {
    completed: { bg: '#e8f5e9', color: '#27ae60' },
    running: { bg: '#e3f2fd', color: '#0079bf' },
    failed: { bg: '#ffebee', color: '#e74c3c' },
    pending: { bg: '#f5f5f5', color: '#999' },
};

export default function AdminBriefings() {
    const { data: runs = [], isLoading: runsLoading } = useBriefingRuns();
    const { data: stats, isLoading: statsLoading } = useBriefingStats();
    const [sort, setSort] = useState({ key: 'timestamp_started', dir: 'desc' });
    const [expanded, setExpanded] = useState({});
    const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    const sorted = sortRows(runs, sort.key, sort.dir, (r, k) => {
        if (k === 'timestamp_started') return r.timestamp_started;
        if (k === 'briefing_name') return r.briefing_name ?? '';
        if (k === 'username') return r.username ?? '';
        if (k === 'status') return r.status ?? '';
        if (k === 'articles_found') return r.articles_found ?? 0;
        return '';
    });

    const sp = (key) => ({
        active: sort.key === key,
        direction: sort.key === key ? sort.dir : 'asc',
        onClick: () => setSort(prev => toggleDir(prev, key)),
    });

    const isLoading = runsLoading || statsLoading;

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    const formatTokens = (n) => {
        if (!n) return '0';
        if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
        return n.toString();
    };

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Smart Brifinzi — Administracija
            </Typography>

            {/* Summary cards */}
            {stats && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Aktivni briefingovi</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0079bf' }}>
                            {stats.active_briefings} / {stats.total_briefings}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Pokretanja danas</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                            {stats.runs_today}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Greške danas</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: stats.errors_today > 0 ? '#e74c3c' : '#27ae60' }}>
                            {stats.errors_today}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Tokeni danas</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                            {formatTokens(stats.tokens_today?.total)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            in: {formatTokens(stats.tokens_today?.input)} · out: {formatTokens(stats.tokens_today?.output)}
                        </Typography>
                    </Paper>
                </Box>
            )}

            {/* Runs table */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#2c3e50' }}>
                Zadnjih 50 pokretanja
            </Typography>
            <Paper sx={{ overflow: 'auto' }}>
                {runs.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                        <Typography color="text.secondary">Nema evidentiranih pokretanja briefingova.</Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel {...sp('timestamp_started')}>Vrijeme</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel {...sp('briefing_name')}>Briefing</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel {...sp('username')}>Korisnik</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel {...sp('status')}>Status</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    <TableSortLabel {...sp('articles_found')}>Članci</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Klasteri</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Tokeni</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Primatelji</TableCell>
                                <TableCell sx={{ width: 40 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((run) => {
                                const sc = STATUS_COLORS[run.status] || STATUS_COLORS.pending;
                                const tokens = run.token_usage;
                                return (
                                    <React.Fragment key={run.id}>
                                        <TableRow hover onClick={() => toggle(run.id)} sx={{ cursor: 'pointer' }}>
                                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                                                {formatDate(run.timestamp_started)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, fontWeight: 600, color: '#0079bf' }}>
                                                {run.briefing_name}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#555' }}>
                                                {run.username}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={run.status}
                                                    size="small"
                                                    sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 600, fontSize: 11 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{run.articles_found ?? '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{run.clusters_formed ?? '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {tokens ? (
                                                    <Tooltip title={`In: ${tokens.input_tokens?.toLocaleString()} · Out: ${tokens.output_tokens?.toLocaleString()}`}>
                                                        <span>{formatTokens((tokens.input_tokens || 0) + (tokens.output_tokens || 0))}</span>
                                                    </Tooltip>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {run.recipients?.length || 0}
                                            </TableCell>
                                            <TableCell align="right" sx={{ p: 0.5 }}>
                                                <IconButton size="small">
                                                    <ExpandMoreIcon fontSize="small" sx={{ transform: expanded[run.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ py: 0, border: expanded[run.id] ? undefined : 0 }}>
                                                <Collapse in={!!expanded[run.id]} unmountOnExit>
                                                    <Box sx={{ p: 2 }}>
                                                        {run.error_message && (
                                                            <Box component="pre" sx={{ fontSize: 12, backgroundColor: '#1e1e1e', color: '#f8f8f2', p: 2, borderRadius: 1, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', mb: 2 }}>
                                                                {run.error_message}
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: 13 }}>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">Završeno</Typography>
                                                                <Typography variant="body2">{formatDate(run.timestamp_completed)}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">Primatelji</Typography>
                                                                <Typography variant="body2">{run.recipients?.join(', ') || '—'}</Typography>
                                                            </Box>
                                                            {tokens && (
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary">Model</Typography>
                                                                    <Typography variant="body2">{tokens.model || '—'}</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
}
